import { ReactNode } from 'react';
import { HtmlGenericElement } from '../html-generic-element/html-generic-element';
import { Link } from 'react-router-dom';
import { cva, type VariantProps } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

export const buttonCva = cva(
  'inline-flex items-center font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 border shadow-sm',
  {
    variants: {
      variant: {
        primary:
          'bg-light-primary hover:bg-light-primary/50 focus:ring-light-primary text-white',
        secondary:
          'bg-white hover:bg-light-primaryAccent/50 focus:ring-light-primaryAccent text-primary',
        warning:
          'bg-yellow-500 hover:bg-yellow-600 focus:ring-yellow-400 text-white',
        danger: 'bg-red-600 hover:bg-red-700 focus:ring-red-500 text-white',
        link: 'mx-4 my-2 p-0 text-indigo-600 hover:text-indigo-800 hover:underline shadow-none border-none focus:ring-0',
        text: 'mx-4 my-2 p-0 text-gray-600 hover:text-gray-800 hover:underline shadow-none border-none focus:ring-0',
      },
      fullWidth: {
        true: 'w-full flex items-center justify-center',
      },
      disabled: {
        true: 'disabled:bg-gray-300 disabled:cursor-not-allowed',
      },
      size: {
        small: 'text-xs px-2 py-1',
        medium: 'text-sm px-4 py-2',
        large: 'text-lg px-6 py-3',
      },
    },
    compoundVariants: [
      { variant: 'text', fullWidth: true, class: 'w-full text-center' },
      { variant: 'link', fullWidth: true, class: 'w-full text-center' },
      { variant: 'primary', fullWidth: true, class: 'w-full text-center' },
      { variant: 'secondary', fullWidth: true, class: 'w-full text-center' },
      { variant: 'warning', fullWidth: true, class: 'w-full text-center' },
      { variant: 'danger', fullWidth: true, class: 'w-full text-center' },
      { variant: 'link', size: 'small', class: 'text-xs mx-2 my-1 px-0 py-0' },
      { variant: 'link', size: 'medium', class: 'text-sm mx-4 my-2 px-0 py-0' },
      { variant: 'link', size: 'large', class: 'text-lg mx-6 my-3 px-0 py-0' },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'medium',
    },
  }
);

export interface ButtonProps extends VariantProps<typeof buttonCva> {
  id?: string;
  children: ReactNode;
  href?: string;
  to?: string;
  onClick?: (e: any) => void;
  className?: string;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  onBlur?: (e: any) => void;
}

export const Button = ({
  id,
  children,
  href,
  to,
  onClick,
  className,
  disabled,
  type = 'button',
  variant,
  size,
  fullWidth,
  onBlur,
}: ButtonProps) => {
  return (
    <HtmlGenericElement
      id={id}
      as={onClick ? 'button' : href ? 'a' : to ? Link : 'button'}
      type={onClick ? 'button' : type}
      onClick={onClick ? onClick : undefined}
      href={href}
      to={to}
      className={twMerge(
        buttonCva({ variant, size, fullWidth, disabled }),
        className
      )}
      disabled={disabled}
      onBlur={onBlur}
    >
      {children}
    </HtmlGenericElement>
  );
};

export default Button;
